import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Settings, Upload, Save, Building2, Sliders, Car, Wrench, Shield } from "lucide-react";
import { Button, Input, Select, Textarea, Card, Badge, Modal, Spinner, FileUploadInput } from "../shared/ui";
import { useAuth } from "../App";
import TicketTypesPanel from "../components/tickets/TicketTypesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    company_name: "",
    company_logo_url: "",
    company_phone: "",
    company_email: "",
    company_address: "",
    default_geofence_radius: 100,
    business_hours_start: "07:00",
    business_hours_end: "17:00",
    timezone: "Australia/Sydney",
    require_swms_signature: true,
    allow_self_clock_out: true,
    overtime_threshold_hours: 8.0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("company");
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const handleBackfillJobTitles = async () => {
    if (!window.confirm("Walk every time entry that's missing a job name and write it back from the Jobs collection? Safe to run repeatedly.")) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await axios.post(`${API}/admin/backfill-time-job-titles`);
      setBackfillResult(res.data);
      toast.success(`Fixed ${res.data.fixed} entries (${res.data.skipped_orphaned} skipped)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      alert("Settings saved successfully!");
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all settings to defaults? This cannot be undone.")) return;
    try {
      await axios.post(`${API}/settings/reset`);
      fetchSettings();
      alert("Settings reset to defaults");
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to reset settings");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Settings</h1>
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Spinner size="sm" /> : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
          </Button>
          <Button variant="ghost" onClick={handleReset}>Reset Defaults</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b-2 border-slate-200">
        {[
          { id: "company", label: "Company Info", icon: Building2 },
          { id: "operations", label: "Operations", icon: Settings },
          { id: "tickets", label: "Tickets", icon: Shield },
          { id: "preferences", label: "Preferences", icon: Sliders },
          { id: "maintenance", label: "Maintenance", icon: Wrench }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 font-semibold text-sm uppercase border-b-2 -mb-[2px] transition-colors ${
              activeTab === tab.id
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Company Info Tab */}
      {activeTab === "company" && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-bold uppercase mb-4">Your Company Details</h3>
            <div className="space-y-4">
              <Input
                label="Company Name"
                value={settings.company_name}
                onChange={(e) => setSettings({...settings, company_name: e.target.value})}
                placeholder="e.g., Mirri Cranes and Rigging Services"
                data-testid="company-name-input"
              />
              <FileUploadInput
                label="Company Logo"
                accept=".jpg,.jpeg,.png,.gif,.svg"
                onFileSelect={(fileUrl, fileName) => setSettings({...settings, company_logo_url: fileUrl})}
                currentFile={settings.company_logo_url ? "Logo uploaded" : null}
                onClear={() => setSettings({...settings, company_logo_url: ""})}
              />
              {settings.company_logo_url && (
                <div className="p-4 bg-slate-100 rounded-lg">
                  <p className="text-xs text-slate-500 mb-2">Logo Preview:</p>
                  <img src={settings.company_logo_url} alt="Company Logo" className="max-h-16 object-contain" />
                </div>
              )}
              <p className="text-xs text-slate-500">This info appears on reports and documents. The app will always be branded as "ShadowOps".</p>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-bold uppercase mb-4">Contact Information</h3>
            <div className="space-y-4">
              <Input
                label="Phone Number"
                value={settings.company_phone}
                onChange={(e) => setSettings({...settings, company_phone: e.target.value})}
                placeholder="+61 400 000 000"
              />
              <Input
                label="Email Address"
                value={settings.company_email}
                onChange={(e) => setSettings({...settings, company_email: e.target.value})}
                placeholder="admin@company.com"
              />
              <Textarea
                label="Address"
                value={settings.company_address}
                onChange={(e) => setSettings({...settings, company_address: e.target.value})}
                placeholder="123 Main Street, Sydney NSW 2000"
              />
            </div>
          </Card>
        </div>
      )}

      {/* Operations Tab */}
      {activeTab === "operations" && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-bold uppercase mb-4">GPS & Location</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Default Geofence Radius (meters)</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="50"
                    max="500"
                    step="25"
                    value={settings.default_geofence_radius}
                    onChange={(e) => setSettings({...settings, default_geofence_radius: parseInt(e.target.value)})}
                    className="flex-1"
                  />
                  <span className="text-lg font-bold text-orange-600 w-20 text-right">{settings.default_geofence_radius}m</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">How close workers need to be to clock in at GPS-enabled jobs</p>
              </div>
              <Select
                label="Timezone"
                value={settings.timezone}
                onChange={(e) => setSettings({...settings, timezone: e.target.value})}
                options={[
                  { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
                  { value: "Australia/Melbourne", label: "Melbourne (AEST/AEDT)" },
                  { value: "Australia/Brisbane", label: "Brisbane (AEST)" },
                  { value: "Australia/Perth", label: "Perth (AWST)" },
                  { value: "Australia/Adelaide", label: "Adelaide (ACST/ACDT)" },
                  { value: "Australia/Darwin", label: "Darwin (ACST)" },
                  { value: "Pacific/Auckland", label: "New Zealand (NZST/NZDT)" }
                ]}
              />
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-bold uppercase mb-4">Business Hours</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Start Time"
                  type="time"
                  value={settings.business_hours_start}
                  onChange={(e) => setSettings({...settings, business_hours_start: e.target.value})}
                />
                <Input
                  label="End Time"
                  type="time"
                  value={settings.business_hours_end}
                  onChange={(e) => setSettings({...settings, business_hours_end: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Overtime Threshold (hours/day)</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="6"
                    max="12"
                    step="0.5"
                    value={settings.overtime_threshold_hours}
                    onChange={(e) => setSettings({...settings, overtime_threshold_hours: parseFloat(e.target.value)})}
                    className="flex-1"
                  />
                  <span className="text-lg font-bold text-orange-600 w-20 text-right">{settings.overtime_threshold_hours}h</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">Hours worked over this threshold count as overtime</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tickets Tab — Master ticket types + per-machine required tickets */}
      {activeTab === "tickets" && (
        <TicketTypesPanel currentUser={user} />
      )}

      {/* Preferences Tab */}
      {activeTab === "preferences" && (
        <Card>
          <h3 className="text-lg font-bold uppercase mb-4">System Preferences</h3>
          <div className="space-y-6">
            <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
              <div>
                <p className="font-semibold">Require SWMS Signature</p>
                <p className="text-sm text-slate-500">Workers must sign SWMS before clocking in to a job</p>
              </div>
              <input
                type="checkbox"
                checked={settings.require_swms_signature}
                onChange={(e) => setSettings({...settings, require_swms_signature: e.target.checked})}
                className="w-6 h-6"
              />
            </label>

            <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
              <div>
                <p className="font-semibold">Allow Self Clock-Out</p>
                <p className="text-sm text-slate-500">Workers can clock themselves out (if disabled, only admins can clock out)</p>
              </div>
              <input
                type="checkbox"
                checked={settings.allow_self_clock_out}
                onChange={(e) => setSettings({...settings, allow_self_clock_out: e.target.checked})}
                className="w-6 h-6"
              />
            </label>

            <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> More settings will be added as you request new features. Let us know what else you'd like to configure!
              </p>
            </div>
          </div>
        </Card>
      )}

      {activeTab === "maintenance" && (
        <Card className="space-y-6">
          <h2 className="text-xl font-bold uppercase">Admin Maintenance Tools</h2>
          <p className="text-sm text-slate-500">One-click utilities for cleaning up legacy data. Safe to run repeatedly — these only touch records that need fixing.</p>

          <div className="border-2 border-slate-200 rounded-lg p-4 space-y-3" data-testid="backfill-time-tool">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-bold text-base">Fix Legacy Time Entries</p>
                <p className="text-sm text-slate-600 mt-1">
                  Walks every clock-in/out record that is missing the job name and writes it back from the Jobs collection. Use this once after deploying the job-sync bug fix to repair historical entries that show <em>"No job"</em>.
                </p>
              </div>
              <Button
                onClick={handleBackfillJobTitles}
                disabled={backfilling}
                data-testid="run-backfill-btn"
              >
                {backfilling ? <Spinner size="sm" /> : <><Wrench className="w-4 h-4 mr-2" />Run Backfill</>}
              </Button>
            </div>
            {backfillResult && (
              <div className="bg-green-50 border-2 border-green-200 rounded-md p-3 text-sm" data-testid="backfill-result">
                <p className="font-bold text-green-800">
                  Done! Fixed <span className="text-2xl">{backfillResult.fixed}</span> entries.
                </p>
                {backfillResult.skipped_orphaned > 0 && (
                  <p className="text-green-700 mt-1">
                    {backfillResult.skipped_orphaned} entry(ies) skipped — those reference jobs that have been deleted.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> These tools only run on the environment they're triggered from. Run them once per environment after a deploy.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};

// Weekly Reports Page (Supervisor only)


export default SettingsPage;
